<template>
    <transition name="update-slide">
        <div v-if="visible" class="app-update-overlay" role="dialog" aria-live="assertive">
            <div class="app-update-card">
                <div class="app-update-glow"></div>

                <div class="app-update-icon">
                    <div class="app-update-icon-inner">
                        <feather-icon icon="RefreshCwIcon" size="28" />
                    </div>
                </div>

                <div class="app-update-body">
                    <div class="app-update-title">
                        Nova versão disponível
                    </div>
                    <div class="app-update-message">
                        Você está utilizando uma versão desatualizada do VeltaCar.
                        Para evitar problemas e ter acesso às melhorias mais recentes,
                        atualize agora — leva apenas alguns segundos.
                    </div>

                    <div class="app-update-actions">
                        <button
                            type="button"
                            class="btn-update"
                            :disabled="updating"
                            @click="onUpdate"
                        >
                            <span v-if="!updating" class="btn-update-content">
                                <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
                                Atualizar agora
                            </span>
                            <span v-else class="btn-update-content">
                                <span class="btn-spinner"></span>
                                Atualizando…
                            </span>
                        </button>
                        <button
                            type="button"
                            class="btn-later"
                            :disabled="updating"
                            @click="onLater"
                        >
                            Mais tarde
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import {
    buildVersionState,
    dismissUpdate,
    forceApplyUpdate,
} from '@/services/buildVersionChecker'

export default {
    name: 'AppUpdateAvailable',
    data() {
        return {
            updating: false,
            state: buildVersionState,
        }
    },
    computed: {
        visible() {
            return this.state.updateAvailable || this.updating
        },
    },
    methods: {
        async onUpdate() {
            this.updating = true
            try {
                await forceApplyUpdate()
            } catch (e) {
                this.updating = false
            }
        },
        onLater() {
            dismissUpdate()
        },
    },
}
</script>

<style scoped lang="scss">
.app-update-overlay {
    position: fixed;
    inset: 0;
    z-index: 10050;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 24px;
    pointer-events: none;
    background: linear-gradient(
        to top,
        rgba(15, 14, 40, 0.35) 0%,
        rgba(15, 14, 40, 0.0) 40%
    );
}

.app-update-card {
    pointer-events: auto;
    position: relative;
    width: 100%;
    max-width: 480px;
    background: #ffffff;
    border-radius: 18px;
    padding: 26px 28px 24px 28px;
    box-shadow:
        0 20px 60px -10px rgba(115, 103, 240, 0.35),
        0 8px 24px -8px rgba(15, 14, 40, 0.20);
    border: 1px solid rgba(115, 103, 240, 0.12);
    overflow: hidden;
    display: flex;
    gap: 18px;
    align-items: flex-start;
}

.app-update-glow {
    position: absolute;
    top: -60px;
    right: -60px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(115, 103, 240, 0.18) 0%, transparent 70%);
    pointer-events: none;
}

.app-update-icon {
    flex-shrink: 0;
}

.app-update-icon-inner {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 18px -4px rgba(115, 103, 240, 0.55);
    animation: update-pulse 2.4s ease-in-out infinite;
}

.app-update-body {
    flex: 1;
    min-width: 0;
}

.app-update-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2c2854;
    margin-bottom: 6px;
    letter-spacing: 0.2px;
}

.app-update-message {
    font-size: 0.9rem;
    line-height: 1.55;
    color: #5e5873;
    margin-bottom: 18px;
}

.app-update-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.btn-update {
    flex: 1 1 180px;
    min-width: 160px;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    padding: 11px 18px;
    font-weight: 600;
    font-size: 0.92rem;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
    box-shadow: 0 6px 16px -4px rgba(115, 103, 240, 0.55);

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 10px 22px -6px rgba(115, 103, 240, 0.65);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.85;
        cursor: progress;
    }
}

.btn-update-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.btn-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-top-color: #ffffff;
    margin-right: 8px;
    animation: update-spin 0.8s linear infinite;
}

.btn-later {
    flex: 0 0 auto;
    background: transparent;
    color: #6e6b7b;
    border: 1px solid #d8d6e0;
    border-radius: 10px;
    padding: 11px 16px;
    font-weight: 500;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;

    &:hover:not(:disabled) {
        background: #f6f5fb;
        border-color: #c4c0d4;
        color: #2c2854;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.update-slide-enter-active,
.update-slide-leave-active {
    transition: opacity 0.3s ease;

    .app-update-card {
        transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease;
    }
}

.update-slide-enter,
.update-slide-leave-to {
    opacity: 0;

    .app-update-card {
        transform: translateY(24px) scale(0.98);
        opacity: 0;
    }
}

@keyframes update-pulse {
    0%, 100% {
        transform: scale(1);
        box-shadow: 0 8px 18px -4px rgba(115, 103, 240, 0.55);
    }
    50% {
        transform: scale(1.05);
        box-shadow: 0 12px 26px -4px rgba(115, 103, 240, 0.70);
    }
}

@keyframes update-spin {
    to { transform: rotate(360deg); }
}

@media (max-width: 540px) {
    .app-update-overlay {
        padding: 12px;
    }

    .app-update-card {
        padding: 20px 20px 18px 20px;
        gap: 14px;
    }

    .app-update-icon-inner {
        width: 44px;
        height: 44px;
        border-radius: 12px;
    }

    .app-update-title {
        font-size: 1rem;
    }

    .app-update-message {
        font-size: 0.85rem;
    }

    .btn-update,
    .btn-later {
        flex: 1 1 100%;
    }
}
</style>
